<template>
  <div class="labelled-input-container" :class="{ isFocused }">
    <label :for="id" @click="onLabelClicked">{{ label }}</label>
    <input
      :id="id"
      ref="inputEl"
      :value="modelValue"
      type="text"
      @keydown="onKeydown"
      @focus="onFocus"
      @blur="onBlur"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";

const props = defineProps<{
  max?: number;
  label: string;
  id?: string;
  type: "text" | "number";
  modelValue: string | number | null;
}>();

const emit = defineEmits<{
  (e: "update:model-value", value: string | number): void;
  (e: "focus", event: FocusEvent): void;
  (e: "blur", event: FocusEvent): void;
}>();

const inputEl = ref<HTMLInputElement>();

const modelValue = computed({
  get() {
    return String(props.modelValue ?? 0);
  },
  set(val: string | number) {
    emit("update:model-value", val);
  }
});

function onLabelClicked() {
  if (inputEl.value) {
    inputEl.value.select();
  }
}

const isFocused = ref(false);

function onBlur(event: FocusEvent) {
  isFocused.value = false;
  if (inputEl.value) {
    const numberValue = Number(inputEl.value.value);
    modelValue.value =
      props.max !== undefined ? Math.min(props.max, numberValue) : numberValue;
  }
  emit("blur", event);
}

function onFocus(e: FocusEvent) {
  isFocused.value = true;
  if (inputEl.value) {
    inputEl.value.select();
  }
  emit("focus", e);
}

function onKeydown(event: KeyboardEvent) {
  const permitRegex = /^[0-9.,-]$/;

  if (!permitRegex.test(event.key) && !isSpecialKey(event) && !event.ctrlKey) {
    event.preventDefault();
  }
}

function isSpecialKey(event: KeyboardEvent) {
  // List of special keys that should not be blocked
  const specialKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Tab",
    "Enter",
    "Shift",
    "Control",
    "Alt",
    "Meta"
  ];

  return specialKeys.includes(event.key);
}
</script>

<style lang="scss" scoped>
.labelled-input-container {
  display: grid;
  grid-template-rows: max-content 1fr;

  border: 1px solid var(--border-main);
  border-radius: 4px;
  box-shadow: var(--shadow-main);
  background: var(--surface-25);
  transition: all 0.2s ease-out;

  &.isFocused {
    border-color: var(--info-500);
  }

  > label {
    padding: 0.25em;

    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: var(--gray-700);
  }

  > input {
    padding: 0.25em;
    border: none;
    border-radius: 4px;

    color: var(--gray-900);
    font-weight: 500;
    font-size: 18px;
    font-family: var(--monospace-family);
    background: var(--surface-25);
    outline: none;
  }
}
</style>
